import { BENCHMARK_TYPES, SECTION_META } from "../../constants/benchmarkTypes";

const COUNTRY_FACTORS = {
  "United Arab Emirates": 0.9,
  UAE: 0.9,
  "Saudi Arabia": 0.88,
  India: 0.94,
  "United States": 1.03,
  USA: 1.03,
  "United Kingdom": 1.02,
  UK: 1.02,
  Germany: 1.05,
  France: 1.04,
  China: 0.92,
  Singapore: 1.08,
  Australia: 1.01,
};

const REGION_BY_COUNTRY = {
  "United Arab Emirates": "Middle East",
  UAE: "Middle East",
  "Saudi Arabia": "Middle East",
  Qatar: "Middle East",
  Kuwait: "Middle East",
  Bahrain: "Middle East",
  Oman: "Middle East",
  India: "Asia Pacific",
  China: "Asia Pacific",
  Singapore: "Asia Pacific",
  Japan: "Asia Pacific",
  Australia: "Asia Pacific",
  "United States": "North America",
  USA: "North America",
  Canada: "North America",
  "United Kingdom": "Europe",
  UK: "Europe",
  Germany: "Europe",
  France: "Europe",
};

const REGION_FACTORS = {
  "Middle East": 0.91,
  Europe: 1.04,
  "Asia Pacific": 0.97,
  "North America": 1.02,
  Africa: 0.89,
  "Latin America": 0.93,
};

const SIZE_FACTORS = {
  micro: 0.9,
  small: 0.94,
  medium: 1.0,
  large: 1.05,
  enterprise: 1.08,
};

const INDUSTRY_FACTORS = {
  "Oil & Gas": 0.88,
  "Oil and Gas": 0.88,
  Manufacturing: 0.95,
  "Real Estate": 0.98,
  Construction: 0.93,
  Healthcare: 1.02,
  Finance: 1.06,
  Technology: 1.04,
  Retail: 0.97,
};

const clamp = (n, min = 0, max = 100) => Math.min(max, Math.max(min, n));

const n = (v) => {
  const parsed = Number(v);
  return Number.isFinite(parsed) ? parsed : null;
};

/** Coerce API refs / DTOs / strings into a safe display label. */
export function asDisplayLabel(value, fallback = "") {
  if (value == null || value === "") return fallback;
  if (typeof value === "string" || typeof value === "number") {
    const text = String(value).trim();
    return text && text !== "[object Object]" ? text : fallback;
  }
  if (typeof value === "object") {
    const nested =
      value.name ??
      value.label ??
      value.title ??
      value.range ??
      value.country_name ??
      value.sector_name ??
      value.value;
    if (nested != null && nested !== value) {
      return asDisplayLabel(nested, fallback);
    }
  }
  return fallback;
}

export function extractOrgContext(report = {}) {
  const org =
    report.organizationDetails?.organization ||
    report.organizationDetails?.organizationDetails ||
    {};
  const country = asDisplayLabel(
    org.country ?? report.country,
    "Global"
  );
  const industry = asDisplayLabel(
    org.industry ?? report.sector,
    ""
  );
  const employeeLabel = asDisplayLabel(org.employeeCount, "");

  const employeeStr = employeeLabel.toLowerCase();
  let sizeBucket = "medium";
  if (employeeStr.includes("1-50") || employeeStr.includes("micro")) sizeBucket = "micro";
  else if (employeeStr.includes("51-200") || employeeStr.includes("small")) sizeBucket = "small";
  else if (employeeStr.includes("1000") || employeeStr.includes("enterprise")) sizeBucket = "enterprise";
  else if (employeeStr.includes("201-1000") || employeeStr.includes("large")) sizeBucket = "large";

  const sector = asDisplayLabel(
    report.formSchemaSector ?? report.sector ?? industry,
    industry
  );

  return {
    country,
    region: REGION_BY_COUNTRY[country] || "Global",
    industry,
    sizeBucket,
    sector,
  };
}

export function extractSectionScores(report = {}) {
  return {
    base: n(report.basicSectorSpecificKPI?.score) ?? 0,
    environment: n(report.environmentKPI?.score) ?? 0,
    social: n(report.socialKPI?.score) ?? 0,
    governance: n(report.governanceKPI?.score) ?? 0,
    overall: n(report.score) ?? 0,
  };
}

function sectorBaseline(orgContext) {
  const industryFactor = INDUSTRY_FACTORS[orgContext.industry] || 1;
  return clamp(58 * industryFactor, 42, 72);
}

function referenceForType(benchmarkType, orgContext, priorScores, customTargets) {
  const baseline = sectorBaseline(orgContext);
  const countryFactor = COUNTRY_FACTORS[orgContext.country] || 1;
  const regionFactor = REGION_FACTORS[orgContext.region] || 1;
  const sizeFactor = SIZE_FACTORS[orgContext.sizeBucket] || 1;
  const industryFactor = INDUSTRY_FACTORS[orgContext.industry] || 1;

  const spread = (key, multiplier) => {
    const center = baseline * multiplier;
    return {
      base: clamp(center * (key === "environment" ? 0.96 : 1)),
      environment: clamp(center * (key === "environment" ? 1.04 : 0.98)),
      social: clamp(center * 1.01),
      governance: clamp(center * 0.99),
      overall: clamp(center),
    };
  };

  switch (benchmarkType) {
    case "industry_average":
      return spread("base", 1);
    case "industry_leaders":
      return spread("base", 1.28);
    case "country_average":
      return spread("base", countryFactor);
    case "regional_average":
      return spread("base", regionFactor);
    case "company_size":
      return spread("base", sizeFactor);
    case "organization_type":
      return spread("base", industryFactor);
    case "previous_assessment":
      return priorScores
        ? { ...priorScores, overall: priorScores.overall || baseline }
        : spread("base", 0.95);
    case "global_best_practices":
      return spread("base", 1.35);
    case "custom":
      return customTargets || spread("base", 1.1);
    default:
      return spread("base", 1);
  }
}

function benchmarkKpiValue(schema, benchmarkType, orgContext, priorKpiMap) {
  const bounds = schema?.bounds || {};
  const { LB, UB, MB } = bounds;
  if (LB == null || UB == null) return null;

  const mid = n(MB) ?? (LB + UB) / 2;
  const best = LB;
  const leaders = LB + (UB - LB) * 0.2;
  const countryFactor = COUNTRY_FACTORS[orgContext.country] || 1;

  if (benchmarkType === "previous_assessment") {
    const prior = priorKpiMap?.[schema.code];
    return prior != null ? prior : mid;
  }

  if (benchmarkType === "custom") {
    const custom = priorKpiMap?.[schema.code];
    return custom != null ? custom : mid;
  }

  const map = {
    industry_average: mid,
    industry_leaders: leaders,
    country_average: mid * countryFactor,
    regional_average: mid * (REGION_FACTORS[orgContext.region] || 1),
    company_size: mid * (SIZE_FACTORS[orgContext.sizeBucket] || 1),
    organization_type: mid * (INDUSTRY_FACTORS[orgContext.industry] || 1),
    global_best_practices: best,
  };

  return map[benchmarkType] ?? mid;
}

export function buildKpiComparisons({
  schemas = [],
  submissions = [],
  benchmarkType,
  orgContext,
  priorSubmissions = [],
  customKpiTargets = {},
}) {
  const latestBySchema = {};
  submissions.forEach((sub) => {
    const schemaId = String(sub.schema?._id || sub.schema?.id || sub.schema || "");
    if (!latestBySchema[schemaId] || new Date(sub.createdAt) > new Date(latestBySchema[schemaId].createdAt)) {
      latestBySchema[schemaId] = sub;
    }
  });

  const priorByCode = {};
  priorSubmissions.forEach((sub) => {
    const code = sub.schema?.code || sub.schemaCode;
    if (!code) return;
    const val = sub.calculated?.kpiValue ?? sub.values?.kpiValue;
    if (val != null) priorByCode[code] = val;
  });

  return schemas
    .map((schema) => {
      const schemaId = String(schema._id || schema.id);
      const submission = latestBySchema[schemaId];
      const orgValue = n(submission?.calculated?.kpiValue);
      const orgNormalized = n(submission?.calculated?.normalized);
      const displayOrg =
        orgValue != null
          ? orgValue
          : orgNormalized != null
            ? orgNormalized * 100
            : null;

      const benchmarkValue = benchmarkKpiValue(
        schema,
        benchmarkType,
        orgContext,
        benchmarkType === "custom" ? customKpiTargets : priorByCode,
      );

      if (displayOrg == null && benchmarkValue == null) return null;

      const lowerIsBetter = inferLowerIsBetter(schema);
      let gap = null;
      let gapPercent = null;
      let status = "no_data";

      if (displayOrg != null && benchmarkValue != null && benchmarkValue !== 0) {
        gap = displayOrg - benchmarkValue;
        gapPercent = (gap / Math.abs(benchmarkValue)) * 100;
        if (lowerIsBetter) {
          status = gap <= 0 ? "ahead" : gap <= benchmarkValue * 0.1 ? "on_par" : "behind";
        } else {
          status = gap >= 0 ? "ahead" : Math.abs(gap) <= benchmarkValue * 0.1 ? "on_par" : "behind";
        }
      }

      return {
        code: schema.code,
        name: schema.kpi || schema.name,
        section: normalizeSection(schema.section),
        unit: schema.unit || "",
        orgValue: displayOrg,
        benchmarkValue,
        gap,
        gapPercent,
        status,
        lowerIsBetter,
        weight: schema.weight || 0,
      };
    })
    .filter(Boolean)
    .sort((a, b) => Math.abs(b.gapPercent || 0) - Math.abs(a.gapPercent || 0));
}

function inferLowerIsBetter(schema) {
  const formula = String(schema?.formula || "").toLowerCase();
  const unit = String(schema?.unit || "").toLowerCase();
  if (unit.includes("/") || formula.includes("/")) return true;
  if (unit.includes("%") && formula.includes("recycl")) return false;
  return true;
}

function normalizeSection(section) {
  const s = String(section || "").toLowerCase();
  if (s.includes("environment")) return "environment";
  if (s.includes("social")) return "social";
  if (s.includes("governance") || s.includes("economic")) return "governance";
  return "base";
}

export function buildSectionComparison(report, benchmarkType, options = {}) {
  const orgScores = extractSectionScores(report);
  const orgContext = extractOrgContext(report);
  const priorReport = report?.last6YearsReports?.[0];
  const priorScores = priorReport ? extractSectionScores(priorReport) : null;

  const reference = referenceForType(
    benchmarkType,
    orgContext,
    priorScores,
    options.customSectionTargets,
  );

  return SECTION_META.map((section) => {
    const org = orgScores[section.key] ?? 0;
    const benchmark = reference[section.key] ?? reference.overall;
    const gap = org - benchmark;
    return {
      ...section,
      orgScore: org,
      benchmarkScore: benchmark,
      gap,
      gapPercent: benchmark ? (gap / benchmark) * 100 : 0,
      status: gap >= 2 ? "ahead" : gap <= -2 ? "behind" : "on_par",
    };
  });
}

export function buildBenchmarkPayload({
  report,
  schemas,
  submissions,
  benchmarkType,
  customSectionTargets,
  customKpiTargets,
}) {
  const orgContext = extractOrgContext(report);
  const priorReport = report?.last6YearsReports?.[0];
  const priorSubmissions = optionsPriorSubmissions(priorReport);

  const sections = buildSectionComparison(report, benchmarkType, {
    customSectionTargets,
  });
  const kpis = buildKpiComparisons({
    schemas,
    submissions,
    benchmarkType,
    orgContext,
    priorSubmissions,
    customKpiTargets,
  });

  const orgScores = extractSectionScores(report);
  const reference = referenceForType(
    benchmarkType,
    orgContext,
    priorReport ? extractSectionScores(priorReport) : null,
    customSectionTargets,
  );

  return {
    benchmarkType,
    benchmarkLabel:
      BENCHMARK_TYPES.find((b) => b.id === benchmarkType)?.label || benchmarkType,
    orgContext,
    overall: {
      orgScore: orgScores.overall,
      benchmarkScore: reference.overall,
      gap: orgScores.overall - reference.overall,
    },
    sections,
    kpis,
    priorReportYear: priorReport?.year || null,
    dashboard: buildBenchmarkDashboard(report, {
      overall: {
        orgScore: orgScores.overall,
        benchmarkScore: reference.overall,
        gap: orgScores.overall - reference.overall,
      },
      orgContext,
      sections,
      kpis,
    }),
  };
}

function optionsPriorSubmissions(priorReport) {
  if (!priorReport?.dynamicKPIs) return [];
  return Object.entries(priorReport.dynamicKPIs).map(([code, entry]) => ({
    schema: { code },
    calculated: entry.calculated,
    values: entry.values,
    createdAt: new Date(),
  }));
}

export function generateGapInsights(payload) {
  const insights = [];
  const { sections, kpis, benchmarkLabel, overall, priorReportYear } = payload;

  if (overall.gap >= 5) {
    insights.push({
      type: "positive",
      title: "Overall performance ahead of benchmark",
      text: `Your overall SI score is ${overall.orgScore?.toFixed(1)} — ${overall.gap.toFixed(1)} points above ${benchmarkLabel}.`,
    });
  } else if (overall.gap <= -5) {
    insights.push({
      type: "priority",
      title: "Overall gap to close",
      text: `Your score trails ${benchmarkLabel} by ${Math.abs(overall.gap).toFixed(1)} points. Focus on the largest KPI gaps below.`,
    });
  }

  const weakest = [...sections].sort((a, b) => a.gap - b.gap)[0];
  if (weakest?.gap < -3) {
    insights.push({
      type: "focus",
      title: `${weakest.label} needs attention`,
      text: `This pillar scores ${weakest.orgScore?.toFixed(1)} vs benchmark ${weakest.benchmarkScore?.toFixed(1)} (${weakest.gap.toFixed(1)} pt gap).`,
    });
  }

  const topGaps = kpis.filter((k) => k.status === "behind").slice(0, 3);
  topGaps.forEach((kpi) => {
    insights.push({
      type: "action",
      title: `Improve ${kpi.name}`,
      text: `Current: ${formatVal(kpi.orgValue)} ${kpi.unit} | Benchmark: ${formatVal(kpi.benchmarkValue)} ${kpi.unit}. ${
        kpi.lowerIsBetter ? "Reduce" : "Increase"
      } intensity to close a ${Math.abs(kpi.gapPercent || 0).toFixed(0)}% gap.`,
      code: kpi.code,
    });
  });

  const ahead = kpis.filter((k) => k.status === "ahead").slice(0, 2);
  ahead.forEach((kpi) => {
    insights.push({
      type: "positive",
      title: `Strength: ${kpi.name}`,
      text: `You outperform ${benchmarkLabel} on this KPI. Maintain practices and document for audit.`,
      code: kpi.code,
    });
  });

  if (payload.benchmarkType === "previous_assessment" && priorReportYear) {
    const yoy = overall.orgScore - (payload.sections[0]?.benchmarkScore || 0);
    insights.unshift({
      type: yoy >= 0 ? "positive" : "focus",
      title: `Year-over-year vs ${priorReportYear}`,
      text:
        yoy >= 0
          ? `Overall score improved by ${yoy.toFixed(1)} points since your prior assessment.`
          : `Overall score declined by ${Math.abs(yoy).toFixed(1)} points since ${priorReportYear}. Review changed KPIs.`,
    });
  }

  return insights.slice(0, 8);
}

const INDUSTRY_COHORT_SIZES = {
  "Oil & Gas": 612,
  "Oil and Gas": 612,
  Manufacturing: 1184,
  "Real Estate": 946,
  Construction: 728,
  Healthcare: 892,
  Finance: 1056,
  Technology: 1342,
  Retail: 804,
};

const SIZE_COHORT_SIZES = {
  micro: 142,
  small: 218,
  medium: 356,
  large: 284,
  enterprise: 196,
};

function deriveGradeFromScore(score) {
  if (score == null || !Number.isFinite(score)) return "—";
  if (score >= 80) return "A";
  if (score >= 65) return "B";
  if (score >= 50) return "C";
  if (score >= 35) return "D";
  return "F";
}

function rankFromPercentile(percentile, cohortSize) {
  const pct = clamp(percentile, 1, 99);
  return Math.max(1, Math.round(cohortSize * (1 - pct / 100)));
}

/**
 * Build benchmark dashboard headline metrics (scores, ranks, grade, year).
 */
export function buildBenchmarkDashboard(report = {}, analysis = {}) {
  const orgScore = n(analysis?.overall?.orgScore) ?? n(report?.score) ?? 0;
  const benchmarkScore = n(analysis?.overall?.benchmarkScore) ?? 0;
  const gap = orgScore - benchmarkScore;
  const orgContext = analysis?.orgContext || extractOrgContext(report);

  const sectionAvg =
    (analysis?.sections || []).reduce((sum, s) => sum + (s.orgScore || 0), 0) /
    Math.max((analysis?.sections || []).length, 1);

  const aheadCount = (analysis?.kpis || []).filter((k) => k.status === "ahead").length;
  const kpiBoost = aheadCount * 0.4;

  const percentileRank = clamp(
    Math.round(50 + gap * 1.75 + (orgScore - sectionAvg) * 0.25 + kpiBoost),
    1,
    99,
  );

  const globalCohort = 12847;
  const industryCohort =
    INDUSTRY_COHORT_SIZES[orgContext.industry] ||
    INDUSTRY_COHORT_SIZES[orgContext.sector] ||
    840;
  const organizationCohort = SIZE_COHORT_SIZES[orgContext.sizeBucket] || 256;

  const esgRatingGrade =
    report?.grade?.trim?.()?.[0] || deriveGradeFromScore(orgScore);

  return {
    overallSustainabilityScore: orgScore,
    overallBenchmarkScore: benchmarkScore,
    percentileRank,
    organizationRank: rankFromPercentile(percentileRank, organizationCohort),
    organizationCohort,
    industryRank: rankFromPercentile(percentileRank, industryCohort),
    industryCohort,
    globalRank: rankFromPercentile(percentileRank, globalCohort),
    globalCohort,
    esgRatingGrade,
    assessmentYear: report?.year || new Date().getFullYear(),
    sector: orgContext.sector,
    country: orgContext.country,
  };
}

function formatVal(v) {
  if (v == null) return "—";
  if (Math.abs(v) >= 1000) return v.toLocaleString(undefined, { maximumFractionDigits: 1 });
  return Number(v).toFixed(2);
}
